import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useFieldDebateGenerationStore } from "@/stores/field-debate-generation-store";

export interface ClaimMapping {
  claim_id: string;
  claim_text: string;
  alignment: "supports" | "challenges" | "tangential";
}

export interface Camp {
  id: string;
  label: string;
  description: string;
  position_summary: string;
  exemplar_paper_ids: string[];
  paper_ids: string[];
  your_claims: ClaimMapping[];
}

export interface Tension {
  camp_a: string;
  camp_b: string;
  description: string;
  tension_type: "contradicts" | "qualifies" | "competes";
}

export interface BridgePaper {
  paper_id: string;
  camps: string[];
  role: string;
}

export interface BlindSpot {
  camp_id: string;
  gap: string;
  suggested_direction: string;
}

export interface DebateMap {
  generated_at: string;
  elapsed_seconds?: number;
  camps: Camp[];
  tensions: Tension[];
  bridge_papers: BridgePaper[];
  blind_spots: BlindSpot[];
  cluster_blind_spots?: Record<string, string[]>;
  /** Snapshot of the corpus the debate ran against; absent on pre-fingerprint results. */
  fingerprint?: import("@/lib/resultFingerprint").ResultFingerprint;
  unaligned?: { paper_id: string; title: string; reason: string }[];
  scope?: {
    min_score: number;
    max_papers: number;
    out_of_scope_count: number;
    fallback_all: boolean;
  };
  assignment_meta?: Record<string, { confidence: string; reason: string }>;
  /** FD Stage 2: skeleton map written before the Pass-2 batch completes. */
  partial?: boolean;
  /** Set when the batch build failed — stop polling, offer retry. */
  partial_error?: string | null;
  /** Stage 2.1 hybrid: interactive top-N head duration + coverage. */
  head_elapsed_seconds?: number | null;
  head_paper_count?: number | null;
  merge_meta?: {
    merged_into: string;
    merged_camp_ids: string[];
    original_labels: string[];
    reason: string;
  }[];
  /** FD Stage 3: audit of the last incremental refresh (set by finalize_refresh). */
  refresh_meta?: {
    added: number;
    removed: number;
    refreshed_at: string;
  } | null;
  /**
   * Stamped by the batch poller when a refresh fails against a COMPLETE map
   * (partial_error only covers skeleton maps) — clears the "Refreshing…"
   * chip with an error toast. Cleared by the next successful refresh/rebuild.
   */
  refresh_error?: string | null;
  /** When the camps were derived — refresh rewrites generated_at. Absent on
   *  never-refreshed maps (use generated_at) and on legacy refreshed maps. */
  camps_generated_at?: string | null;
  /**
   * Computed on read by GET /field-debate from field_debate_builds: the
   * latest in-flight batch build. The durable counterpart of the in-memory
   * pendingRefresh flag — survives reloads, so the overview can say
   * "Refreshing…" instead of falling back to STALE.
   */
  pending_build?: { kind: "build" | "refresh"; submitted_at: string } | null;
  /** FD detection shadow mode (spec 2026-07-13): additive annotation record.
   * Owner-only — the shared route strips it server-side. */
  detection?: {
    classification: "DEBATE" | "MIXED" | "TAXONOMY";
    axes: {
      description: string;
      pole_a_camp_ids: string[];
      pole_b_camp_ids: string[];
      pole_a_papers: number;
      pole_b_papers: number;
      joined: boolean;
    }[];
    flags: string[];
    model: string;
    detected_at: string;
  } | null;
}

export function useFieldDebate(conceptId: string) {
  return useQuery({
    queryKey: ["field-debate", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/field-debate`)
        .json<DebateMap>(),
    retry: false,
    // Poll while a batch build is assigning papers (partial skeleton map).
    // A failed build sets partial_error — stop polling, the UI offers retry.
    refetchInterval: (query) => {
      const d = query.state.data as DebateMap | undefined;
      if (d?.partial && !d.partial_error) return 20_000;
      // FD Stage 3: during a pending batch REFRESH the map stays complete
      // (non-partial), so the partial poll above never fires. Poll while the
      // generation store says a refresh batch is in flight for this concept;
      // FieldDebateView clears the flag (and toasts) once a fetched map's
      // refresh_meta.refreshed_at advances past pendingRefresh.since.
      const pending = useFieldDebateGenerationStore.getState().pendingRefresh;
      if (pending?.conceptId === conceptId && d && !d.partial) return 20_000;
      // Server-reported pending refresh (after a reload the store flag is
      // gone): keep polling until the poller finalizes and clears it.
      if (d?.pending_build?.kind === "refresh" && !d.partial) return 20_000;
      return false;
    },
    // Guard the empty-id case so global callers (e.g. useLiveSuggestions in the
    // always-mounted Where-am-I overlay) can pass `conceptId ?? ""` without
    // firing a bad request. Existing callers always pass a real id.
    enabled: !!conceptId,
  });
}

export interface PlacementFeedbackPayload {
  paper_id: string;
  camp_id: string;
  signal?: "thumbs_down" | "thumbs_up";
}

/** Placement thumbs on one paper's camp assignment (trust-first slice).
 * Fire-and-forget FeedbackEvent write — no cache to update; the caller owns
 * the flagged-row local state (feedback given ≠ map data). */
export function usePlacementFeedback(conceptId: string) {
  return useMutation({
    mutationFn: (payload: PlacementFeedbackPayload) =>
      apiClient
        .post(`api/concepts/${conceptId}/field-debate/placement-feedback`, {
          json: { signal: "thumbs_down", ...payload },
        })
        .json<{ status: string }>(),
  });
}

export interface DetectionFeedbackPayload {
  signal: "thumbs_up" | "thumbs_down";
  /** detected_at of the record being judged — server 409s on mismatch so a
   * vote can never attach to a newer detection (rebuild race). */
  detected_at: string;
}

/** Thumbs on the detection banner (shadow mode). Fire-and-forget
 * FeedbackEvent write — no cache to update; the banner owns the one-shot
 * voted local state (feedback given ≠ map data). */
export function useDetectionFeedback(conceptId: string) {
  return useMutation({
    mutationFn: (payload: DetectionFeedbackPayload) =>
      apiClient
        .post(`api/concepts/${conceptId}/field-debate/detection-feedback`, {
          json: payload,
        })
        .json<{ status: string }>(),
  });
}

// NOTE: a dead `useGenerateDebate` mutation was deleted here (2026-07-08,
// review rider): zero callers, and its payload lacked `apply_scope` — if
// revived it would silently fall back to the backend's full-set heuristic.
// Live generation goes through useFieldDebateGenerate's SSE `start()`.
